import { useState, useEffect } from "react";
import { useConfirm } from "@/components/ConfirmProvider";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  EMPTY_VEHICLE_FORM,
  VEHICLE_STATUSES,
  SAMPLE_IMAGES,
} from "@/lib/constants";
import Drawer, {
  FormField,
  FormSelect,
  FormTextarea,
  getSaveLabel,
} from "@/components/Drawer";
import VehicleTypeSelector from "@/components/VehicleTypeSelector";
import { PackagesSelector } from "@/components/PackagesSelector";
import BranchSelect from "@/components/BranchSelect";

function vehicleToForm(v) {
  return {
    type: v.type,
    year: v.year,
    vin: v.vin,
    price: v.price,
    color: v.color || "",
    mileage: v.mileage || 0,
    image_url: v.image_url || "",
    status: v.status,
    notes: v.notes || "",
    engine_no: v.engine_no || "",
    purchased_on: v.purchased_on || "",
    dealer_code: v.dealer_code ?? "",
    package_ids: v.package_ids || [],
    branch_id: v.branch_id || "",
  };
}

function VehicleFormFields({ form, setForm }) {
  return (
    <>
      <VehicleTypeSelector value={form.type} onChange={(v) => setForm({ ...form, type: v })} />
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Year" type="number" value={form.year} onChange={(v) => setForm({ ...form, year: v })} testId="vehicle-year" required />
        <FormField label="Color" value={form.color} onChange={(v) => setForm({ ...form, color: v })} testId="vehicle-color" />
        <FormField label="VIN" value={form.vin} onChange={(v) => setForm({ ...form, vin: v.toUpperCase() })} testId="vehicle-vin" required mono />
        <FormField label="Engine No" value={form.engine_no} onChange={(v) => setForm({ ...form, engine_no: v.toUpperCase() })} testId="vehicle-engine-no" mono />
        <FormField label="Date of Purchase" type="date" value={form.purchased_on} onChange={(v) => setForm({ ...form, purchased_on: v })} testId="vehicle-purchased-on" />
        <FormField label="Dealer Code" type="number" value={form.dealer_code} onChange={(v) => setForm({ ...form, dealer_code: v })} testId="vehicle-dealer-code" mono required />
        <FormField label="Mileage" type="number" value={form.mileage} onChange={(v) => setForm({ ...form, mileage: v })} testId="vehicle-mileage" />
        <FormField label="Price (₹)" type="number" value={form.price} onChange={(v) => setForm({ ...form, price: v })} testId="vehicle-price" required />
        <FormSelect label="Status" value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={VEHICLE_STATUSES} testId="vehicle-status" />
      </div>
      <FormField
        label="Image URL"
        value={form.image_url}
        onChange={(v) => setForm({ ...form, image_url: v })}
        testId="vehicle-image"
        placeholder={`Leave blank to use ${form.type} placeholder`}
      />
      <BranchSelect
        value={form.branch_id}
        onChange={(v) => setForm({ ...form, branch_id: v })}
        testId="vehicle-branch"
        required
      />
      <FormTextarea
        label="Notes"
        value={form.notes}
        onChange={(v) => setForm({ ...form, notes: v })}
        testId="vehicle-notes"
      />
      <PackagesSelector
        branchId={form.branch_id}
        selected={form.package_ids}
        onChange={(package_ids) => setForm({ ...form, package_ids })}
        onAvailableIdsChange={(ids) => {
          // When the branch (and hence branch type) changes, drop any
          // attached packages that are no longer in the allowed list.
          if (!form.package_ids?.length) return;
          const allowed = new Set(ids);
          const pruned = form.package_ids.filter((pid) => allowed.has(pid));
          if (pruned.length !== form.package_ids.length) {
            setForm((f) => ({ ...f, package_ids: pruned }));
          }
        }}
      />
    </>
  );
}

export default function VehicleFormDrawer({ open, editing, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_VEHICLE_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(editing ? vehicleToForm(editing) : EMPTY_VEHICLE_FORM);
  }, [open, editing]); // EMPTY_VEHICLE_FORM is module-level constant; setForm is stable.

  const confirm = useConfirm();
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!(await confirm({ title: "Save vehicle?", body: "Vehicle will be added to the inventory master. Confirm before saving." }))) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        year: Number(form.year),
        price: Number(form.price),
        mileage: Number(form.mileage),
        dealer_code: form.dealer_code === "" || form.dealer_code === null
          ? null
          : Number(form.dealer_code),
        image_url: form.image_url || SAMPLE_IMAGES[form.type] || "",
      };
      if (editing) {
        await api.put(`/vehicles/${editing.id}`, payload);
        toast.success("Vehicle updated");
      } else {
        await api.post("/vehicles", payload);
        toast.success("Vehicle added to inventory");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const title = editing
    ? (editing.package_name || (editing.packages?.[0]?.name) || editing.vin || "Vehicle")
    : "New listing";

  return (
    <Drawer
      open={open}
      onClose={onClose}
      eyebrow={`${editing ? "Edit" : "Add"} Vehicle`}
      title={title}
      testIdClose="close-vehicle-form"
    >
      <form onSubmit={handleSubmit} data-testid="vehicle-form" className="space-y-4">
        <VehicleFormFields form={form} setForm={setForm} />
        <div className="pt-4 flex items-center gap-2 justify-end border-t border-canvas-line -mx-6 px-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 border border-canvas-line text-sm hover:border-ink"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="save-vehicle-button"
            className="px-4 py-2.5 bg-ink text-white text-sm font-semibold hover:bg-brand transition-colors disabled:opacity-60"
          >
            {getSaveLabel(saving, editing, "Saving…", "Save changes", "Add to stock")}
          </button>
        </div>
      </form>
    </Drawer>
  );
}
